import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Percent, Plus, History, X, AlertTriangle, CheckCircle2, Clock
} from 'lucide-react';
import { parametrosFiscalesService } from '../../../services/api';

// Cada clave es una tabla real con vigencia (daterange) y bitacora
// obligatoria en el backend (parametros_historial). "Nueva vigencia"
// cierra la fila abierta actual y crea la siguiente -- nunca se edita
// ni se borra una vigencia ya cargada.
const TABS = [
  {
    id: 'alicuotas_iva',
    label: 'IVA',
    campos: [
      { name: 'codigo', label: 'Código', required: true },
      { name: 'nombre', label: 'Nombre', required: true },
      { name: 'porcentaje', label: 'Porcentaje (fracción, ej. 0.16)', type: 'number', step: '0.0001', required: true },
      { name: 'base_legal', label: 'Base legal' },
      { name: 'respaldo', label: 'Respaldo' }
    ]
  },
  {
    id: 'reglas_retencion_iva',
    label: 'Retención IVA',
    campos: [
      { name: 'codigo', label: 'Código', required: true },
      { name: 'nombre', label: 'Nombre', required: true },
      { name: 'porcentaje', label: 'Porcentaje (fracción)', type: 'number', step: '0.0001', required: true },
      { name: 'base_legal', label: 'Base legal' }
    ]
  },
  {
    id: 'reglas_igtf',
    label: 'IGTF',
    campos: [
      { name: 'aplica_a', label: 'Aplica a', type: 'select', options: ['divisa', 'cripto', 'ambos'], required: true },
      { name: 'porcentaje', label: 'Porcentaje (fracción)', type: 'number', step: '0.0001', required: true },
      { name: 'base_legal', label: 'Base legal' }
    ]
  },
  {
    id: 'unidad_tributaria',
    label: 'Unidad Tributaria',
    campos: [
      { name: 'valor', label: 'Valor (Bs)', type: 'number', step: '0.0001', required: true },
      { name: 'moneda', label: 'Moneda', defaultValue: 'VES' },
      { name: 'base_legal', label: 'Base legal' }
    ]
  },
  {
    id: 'retenciones_municipales',
    label: 'Retención Municipal',
    campos: [
      { name: 'municipio', label: 'Municipio', required: true },
      { name: 'codigo', label: 'Código', required: true },
      { name: 'nombre', label: 'Nombre', required: true },
      { name: 'porcentaje', label: 'Porcentaje (fracción)', type: 'number', step: '0.0001', required: true },
      { name: 'base_legal', label: 'Base legal' }
    ]
  },
  {
    id: 'islr_tarifas',
    label: 'Tarifas ISLR',
    campos: [
      { name: 'concepto_id', label: 'Concepto', type: 'concepto', required: true },
      { name: 'tipo_persona', label: 'Tipo de persona', type: 'select', options: ['natural', 'juridica'], required: true },
      { name: 'domiciliado', label: 'Domiciliado', type: 'boolean', defaultValue: true },
      { name: 'porcentaje_base', label: '% de la base (normalmente 1)', type: 'number', step: '0.0001', defaultValue: '1' },
      { name: 'porcentaje', label: 'Porcentaje (fracción)', type: 'number', step: '0.0001', required: true },
      { name: 'sustraendo_ut', label: 'Sustraendo (UT)', type: 'number', step: '0.0001' },
      { name: 'base_minima_ut', label: 'Base mínima (UT)', type: 'number', step: '0.0001' },
      { name: 'base_legal', label: 'Base legal' },
      { name: 'provisional', label: 'Provisional', type: 'boolean', defaultValue: true }
    ]
  },
  {
    id: 'islr_conceptos',
    label: 'Conceptos ISLR',
    nota: 'porcentaje_pn y porcentaje_pj no se editan aquí: los actualiza solo el sistema desde "Tarifas ISLR".',
    campos: [
      { name: 'codigo', label: 'Código', required: true },
      { name: 'nombre', label: 'Nombre', required: true },
      { name: 'descripcion', label: 'Descripción' },
      { name: 'usa_sustraendo', label: 'Usa sustraendo', type: 'boolean', defaultValue: false },
      { name: 'sustraendo_ut', label: 'Sustraendo (UT)', type: 'number', step: '0.0001' },
      { name: 'provisional', label: 'Provisional', type: 'boolean', defaultValue: true },
      { name: 'base_legal', label: 'Base legal' },
      { name: 'activo', label: 'Activo', type: 'boolean', defaultValue: true }
    ]
  }
];

function parseVigencia(v) {
  if (!v) return { desde: null, hasta: null, vigente: false };
  const m = v.match(/^[[(]([^,]*),([^\])]*)[\])]$/);
  if (!m) return { desde: null, hasta: null, vigente: false };
  const desde = m[1] || null;
  const hasta = m[2] || null;
  const hoy = new Date().toISOString().slice(0, 10);
  const vigente = (!desde || desde <= hoy) && (!hasta || hoy < hasta);
  return { desde, hasta, vigente };
}

// new Date('2026-01-01') se interpreta como UTC medianoche; formateada
// en horario de Venezuela (UTC-4) muestra 31/12/2025. Para fechas
// puras (sin hora) hay que parsear el texto, nunca pasar por Date.
const fmtFecha = (f) => {
  if (!f) return '—';
  const [y, m, d] = String(f).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
const fmtValor = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
};

const OPERACION_LABEL = { I: 'Alta', U: 'Modificación', D: 'Cierre/baja' };

const ParametrosFiscalesView = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [filas, setFilas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const [historialAbierto, setHistorialAbierto] = useState(null); // {id, rows}
  const [historialLoading, setHistorialLoading] = useState(false);

  const tab = TABS.find(t => t.id === activeTab);

  const cargarLista = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await parametrosFiscalesService.listar(activeTab);
      setFilas(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  // Conceptos ISLR vigentes, para el selector de "Tarifas ISLR" y para
  // mostrar el nombre del concepto en vez del id crudo.
  useEffect(() => {
    parametrosFiscalesService.listar('islr_conceptos', { vigentes: true })
      .then(res => setConceptos(res.data || []))
      .catch(() => {});
  }, []);

  const conceptoNombre = useMemo(() => {
    const map = {};
    conceptos.forEach(c => { map[c.id] = `${c.nombre} (${c.codigo})`; });
    return map;
  }, [conceptos]);

  const abrirFormulario = () => {
    const inicial = { fecha_desde: '', motivo: '' };
    tab.campos.forEach(c => {
      inicial[c.name] = c.defaultValue !== undefined ? c.defaultValue : (c.type === 'boolean' ? false : '');
    });
    setFormData(inicial);
    setFormError('');
    setShowForm(true);
  };

  const cambiarCampo = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const enviarFormulario = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = { fecha_desde: formData.fecha_desde, motivo: formData.motivo };
      tab.campos.forEach(c => {
        let v = formData[c.name];
        if (c.type === 'number' && v !== '') v = Number(v);
        if (c.type === 'concepto' && v !== '') v = Number(v);
        payload[c.name] = v === '' ? null : v;
      });
      await parametrosFiscalesService.nuevaVigencia(activeTab, payload);
      setShowForm(false);
      await cargarLista();
    } catch (err) {
      setFormError(err.message || 'Error al registrar la nueva vigencia');
    } finally {
      setFormLoading(false);
    }
  };

  const verHistorial = async (id) => {
    setHistorialAbierto({ id, rows: [] });
    setHistorialLoading(true);
    try {
      const res = await parametrosFiscalesService.historial(activeTab, id);
      setHistorialAbierto({ id, rows: res.data || [] });
    } catch (err) {
      setHistorialAbierto({ id, rows: [], error: err.message });
    } finally {
      setHistorialLoading(false);
    }
  };

  return (
    <div className="p-2 md:p-6 space-y-6">
      <div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Percent className="text-blue-600" />
          Parámetros Fiscales
        </h2>
        <p className="text-sm lg:text-base text-gray-600">
          IVA, retenciones, IGTF, unidad tributaria e ISLR. Cada cambio exige un motivo y queda en la bitácora — nunca se edita ni se borra una vigencia ya cargada.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveTab(t.id); setShowForm(false); }}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === t.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab.nota && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-3 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          {tab.nota}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
      )}

      <div className="flex justify-end">
        <button
          onClick={abrirFormulario}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
        >
          <Plus size={16} /> Nueva vigencia
        </button>
      </div>

      {showForm && (
        <FormularioVigencia
          tab={tab}
          formData={formData}
          onChange={cambiarCampo}
          onSubmit={enviarFormulario}
          onCancel={() => setShowForm(false)}
          error={formError}
          loading={formLoading}
          conceptos={conceptos}
        />
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <TablaVigencias tab={tab} filas={filas} conceptoNombre={conceptoNombre} onVerHistorial={verHistorial} />
      )}

      {historialAbierto && (
        <PanelHistorial
          registroId={historialAbierto.id}
          rows={historialAbierto.rows}
          loading={historialLoading}
          error={historialAbierto.error}
          onClose={() => setHistorialAbierto(null)}
        />
      )}
    </div>
  );
};

const FormularioVigencia = ({ tab, formData, onChange, onSubmit, onCancel, error, loading, conceptos }) => (
  <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-gray-800">Nueva vigencia — {tab.label}</h3>
      <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
        <X size={18} />
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {tab.campos.map((c) => (
        <div key={c.name} className={c.type === 'boolean' ? 'flex items-center gap-2 mt-6' : ''}>
          {c.type === 'boolean' ? (
            <>
              <input
                type="checkbox"
                checked={!!formData[c.name]}
                onChange={(e) => onChange(c.name, e.target.checked)}
                id={`campo-${c.name}`}
              />
              <label htmlFor={`campo-${c.name}`} className="text-sm text-gray-700">{c.label}</label>
            </>
          ) : c.type === 'select' ? (
            <>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {c.label}{c.required && ' *'}
              </label>
              <select
                value={formData[c.name] ?? ''}
                onChange={(e) => onChange(c.name, e.target.value)}
                required={c.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccione…</option>
                {c.options.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            </>
          ) : c.type === 'concepto' ? (
            <>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {c.label}{c.required && ' *'}
              </label>
              <select
                value={formData[c.name] ?? ''}
                onChange={(e) => onChange(c.name, e.target.value)}
                required={c.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccione…</option>
                {conceptos.map(cn => (
                  <option key={cn.id} value={cn.id}>{cn.nombre} ({cn.codigo})</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {c.label}{c.required && ' *'}
              </label>
              <input
                type={c.type === 'number' ? 'number' : 'text'}
                step={c.step}
                value={formData[c.name] ?? ''}
                onChange={(e) => onChange(c.name, e.target.value)}
                required={c.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </>
          )}
        </div>
      ))}

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Vigente desde *</label>
        <input
          type="date"
          value={formData.fecha_desde ?? ''}
          onChange={(e) => onChange('fecha_desde', e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Motivo del cambio * (mínimo 10 caracteres)</label>
      <textarea
        value={formData.motivo ?? ''}
        onChange={(e) => onChange('motivo', e.target.value)}
        required
        minLength={10}
        rows={2}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        placeholder="Por qué cambia este parámetro (queda en la bitácora de auditoría)"
      />
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
    )}

    <div className="flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
        Cancelar
      </button>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
      >
        {loading ? 'Guardando…' : 'Registrar nueva vigencia'}
      </button>
    </div>
  </form>
);

const TablaVigencias = ({ tab, filas, conceptoNombre, onVerHistorial }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            <th className="px-4 py-2">Estado</th>
            {tab.campos.map(c => <th key={c.name} className="px-4 py-2">{c.label}</th>)}
            <th className="px-4 py-2">Desde</th>
            <th className="px-4 py-2">Hasta</th>
            <th className="px-4 py-2 text-center">Historial</th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr><td colSpan={tab.campos.length + 4} className="px-4 py-6 text-center text-gray-400">Sin vigencias cargadas</td></tr>
          ) : filas.map((f) => {
            const { desde, hasta, vigente } = parseVigencia(f.vigencia);
            return (
              <tr key={f.id} className={`border-b border-gray-100 ${vigente ? '' : 'text-gray-400'}`}>
                <td className="px-4 py-2">
                  {vigente ? (
                    <span className="inline-flex items-center gap-1 text-green-700 font-medium text-xs">
                      <CheckCircle2 size={14} /> Vigente
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400 text-xs">
                      <Clock size={14} /> Histórica
                    </span>
                  )}
                </td>
                {tab.campos.map(c => (
                  <td key={c.name} className="px-4 py-2">
                    {c.type === 'concepto' ? (conceptoNombre[f[c.name]] || f[c.name]) : fmtValor(f[c.name])}
                  </td>
                ))}
                <td className="px-4 py-2">{fmtFecha(desde)}</td>
                <td className="px-4 py-2">{hasta ? fmtFecha(hasta) : '—'}</td>
                <td className="px-4 py-2 text-center">
                  <button onClick={() => onVerHistorial(f.id)} className="text-gray-400 hover:text-blue-600">
                    <History size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

function camposCambiados(antes, despues) {
  if (!antes) return Object.keys(despues || {}).filter(k => !['id', 'created_at', 'updated_at'].includes(k));
  if (!despues) return [];
  const claves = new Set([...Object.keys(antes), ...Object.keys(despues)]);
  return [...claves].filter(k => !['id', 'created_at', 'updated_at'].includes(k) && JSON.stringify(antes[k]) !== JSON.stringify(despues[k]));
}

const PanelHistorial = ({ registroId, rows, loading, error, onClose }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
        <h3 className="font-semibold text-gray-800">Historial — registro #{registroId}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      <div className="p-5 space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Sin movimientos registrados</p>
        ) : rows.map((r) => {
          const cambios = camposCambiados(r.valor_antes, r.valor_despues);
          return (
            <div key={r.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-800">{OPERACION_LABEL[r.operacion] || r.operacion}</span>
                <span className="text-xs text-gray-400">{new Date(r.ocurrido_en).toLocaleString('es-VE')}</span>
              </div>
              <p className="text-gray-600 mb-2">{r.motivo}</p>
              <p className="text-xs text-gray-400 mb-2">Por: {r.usuario_nombre || 'desconocido'}</p>
              {cambios.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-2 space-y-1">
                  {cambios.map(campo => (
                    <div key={campo} className="flex gap-2 text-xs">
                      <span className="text-gray-500 w-32 flex-shrink-0">{campo}</span>
                      <span className="text-gray-400">{fmtValor(r.valor_antes?.[campo])}</span>
                      <span>→</span>
                      <span className="text-gray-800 font-medium">{fmtValor(r.valor_despues?.[campo])}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

export default ParametrosFiscalesView;
